/**
 * Inaction / cooling configuration
 *
 * All thresholds are centralized here so we can tune them
 * without touching UI components or business logic.
 *
 * SLA values are expressed in hours.
 */

export type InactionSourceCategory = 'inbound' | 'referral' | 'outbound' | 'unknown';

/**
 * Temperature-based SLA (hours) for "due" calculation.
 * Used when next_follow_up_at is not set: dueAt = anchorAt + SLA.
 * Single source of truth for HOT/WARM/COLD/KEEP_WARM.
 */
export type LeadTemperature = 'hot' | 'warm' | 'cold' | 'keep_warm';

export const LEAD_SLA_HOURS: Record<LeadTemperature, number> = {
  hot: 6,
  warm: 24,
  cold: 72,
  keep_warm: 72, // 3d
};

/** Default delay (days) before a lead is "due" for re-engagement in Keep Warm queue. */
export const KEEP_WARM_DELAY_DAYS = 3;

/** Keep Warm delay in milliseconds (lastOutboundAt + this = keep_warm_due_at). */
export function getKeepWarmDelayMs(): number {
  return KEEP_WARM_DELAY_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Returns SLA in milliseconds for a lead based on temperature.
 * Fallback: cold (72h) if temperature missing or unknown.
 */
export function getSlaMs(lead: { temperature?: string | null }): number {
  const t = (lead.temperature ?? 'cold').toLowerCase() as LeadTemperature;
  const hours = LEAD_SLA_HOURS[t] ?? LEAD_SLA_HOURS.cold;
  return hours * 60 * 60 * 1000;
}

/**
 * First-touch SLA by lead source category (legacy; prefer temperature-based for due date).
 *
 * NOTE: If we don't have an explicit source type on the lead,
 * we default to "unknown" which currently matches outbound (24h).
 * This keeps behaviour stable while allowing future refinement.
 */
export const INACTION_SLA_HOURS: Record<InactionSourceCategory, number> = {
  inbound: 2,   // Inbound / demo requests: respond within 2h
  referral: 4,  // Warm referrals: respond within 4h
  outbound: 24, // Cold / outbound / unknown: respond within 24h
  unknown: 24,
};

/**
 * Time buckets relative to SLA, used to determine
 * FRESH / DUE / OVERDUE / STALE states.
 *
 * All values are in hours.
 */
export const INACTION_THRESHOLDS = {
  /**
   * Fraction of SLA below which a lead is considered FRESH.
   * Example: SLA 24h, freshUntil = 12h.
   */
  freshFraction: 0.5,

  /**
   * Maximum window after SLA where we still consider a lead OVERDUE
   * before it becomes STALE.
   *
   * Example: SLA 24h, overdue window 72h => STALE after 96h.
   */
  overdueWindowHours: 72,
};

/**
 * Severity mapping per inaction state.
 *
 * This is UI-only and *must not* be used for core prioritization.
 * Range: 0–100.
 */
export const INACTION_SEVERITY_BASE: Record<'FRESH' | 'DUE' | 'OVERDUE' | 'STALE', number> = {
  FRESH: 20,
  DUE: 50,
  OVERDUE: 80,
  STALE: 95,
};

/**
 * Utility to clamp a numeric value into [min, max].
 */
export function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

